import { useState, useEffect } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, SectionHeading, Accent } from "@/components/Reveal";
import { ARTICLES } from "@/lib/content";
import { useLang } from "@/lib/i18n";
import { api, API } from "@/lib/api";

const localize = (a, lang) => {
  if (lang === "en" && a.title_en) {
    return { ...a, title: a.title_en, excerpt: a.excerpt_en || a.excerpt, body: a.body_en?.length ? a.body_en : a.body, readTime: a.read_time_en || a.readTime, localized: true };
  }
  return { ...a, localized: lang === "bg" };
};

export const Blog = () => {
  const [openId, setOpenId] = useState(null);
  const [raw, setRaw] = useState(ARTICLES);
  const { t, lang } = useLang();
  const articles = raw.map((a) => localize(a, lang));

  useEffect(() => {
    api
      .get("/articles")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          setRaw(data.map((a) => ({ ...a, readTime: a.read_time })));
        }
      })
      .catch(() => {});
  }, []);

  const active = articles.find((a) => a.id === openId);

  return (
    <section id="blog" data-testid="blog-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
      <SectionHeading kicker={t.blog.kicker} title={<Accent parts={t.blog.title} />} testid="blog-heading" />

      <div className="grid md:grid-cols-3 gap-6 md:gap-8">
        {articles.map((a, i) => (
          <Reveal key={a.id} delay={i * 0.08} className="h-full">
            <article
              data-testid={`article-card-${a.id}`}
              className="group h-full border border-line bg-ink p-8 flex flex-col transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-gold/40"
            >
              {a.cover && (
                <img
                  src={`${API}/files/${a.cover}`}
                  alt={a.title}
                  loading="lazy"
                  className="-mx-8 -mt-8 mb-6 w-[calc(100%+4rem)] max-w-none aspect-[16/9] object-cover grayscale-[0.35] group-hover:grayscale-0 transition-[filter] duration-700 border-b border-line"
                />
              )}
              <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-dim mb-6">
                <span>{a.date}</span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={12} /> {a.readTime}
                </span>
                {!a.localized && (
                  <span data-testid={`article-lang-badge-${a.id}`} className="ml-auto border border-line px-2 py-0.5 text-[10px] text-dim/70">BG</span>
                )}
              </div>
              <h3 className="font-serif text-2xl text-mist leading-snug mb-4 group-hover:text-gold-light transition-colors duration-300">
                {a.title}
              </h3>
              <p className="text-dim text-sm leading-relaxed flex-1 mb-8">{a.excerpt}</p>
              <button
                data-testid={`article-read-${a.id}`}
                onClick={() => setOpenId(a.id)}
                className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-light transition-colors duration-300"
              >
                {t.blog.read}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </article>
          </Reveal>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setOpenId(null)}>
        <DialogContent className="bg-coal border-line text-mist max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="article-dialog">
          {active && (
            <>
              <DialogHeader>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-3">
                  {active.date} · {active.readTime} {t.blog.reading}
                </p>
                <DialogTitle className="font-serif text-3xl text-mist leading-tight">{active.title}</DialogTitle>
                <DialogDescription className="sr-only">{active.excerpt}</DialogDescription>
              </DialogHeader>
              {active.cover && (
                <img
                  src={`${API}/files/${active.cover}`}
                  alt={active.title}
                  className="w-full aspect-[16/9] object-cover border border-line"
                />
              )}
              <div className="flex flex-col gap-5 mt-4">
                {active.body.map((p, i) => (
                  <p key={i} className="text-dim leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
